import { getScoreColor } from '../utils/scoring';

export default function ConceptDrift({ audit }) {
  const ai = audit.aiResearch || {};
  const intake = audit.intake || {};

  // Extract concept drift signals from AI data
  const brandCoherence = ai.brandCoherence || audit.brandCoherence || {};
  const sentiment = ai.guestSentiment || {};
  const googleSentiment = sentiment.google || sentiment;

  // What the business says it is
  const brandClaims = [
    intake.conceptName && `Concept: "${intake.conceptName}"`,
    intake.website && `Website: ${intake.website}`,
    intake.instagram && `Instagram: ${intake.instagram}`,
    brandCoherence.websiteAlign && `Website message: ${brandCoherence.websiteAlign}`,
    brandCoherence.menuAlign && `Menu message: ${brandCoherence.menuAlign}`,
    brandCoherence.socialAlign && `Social message: ${brandCoherence.socialAlign}`,
  ].filter(Boolean);

  // What guests actually say
  const guestReality = [
    ...(googleSentiment.positiveThemes || []).filter(Boolean).map(t => `+ ${t}`),
    ...(googleSentiment.negativeThemes || []).filter(Boolean).map(t => `- ${t}`),
  ];

  // Drift indicators from score tensions
  const tensions = (ai.scoreTensions || []).filter(Boolean).map(t =>
    typeof t === 'string' ? t : t.tension || t.description || ''
  );

  // Calculate drift score
  const conceptScore = audit.scores?.concept_clarity?.score || 0;
  const brandScore = audit.scores?.brand_expression?.score || 0;
  const guestScore = audit.scores?.guest_signal?.score || 0;
  const driftScore = conceptScore > 0 && brandScore > 0 ? Math.abs(conceptScore - guestScore) : 0;
  const driftLevel = driftScore > 3 ? 'Severe' : driftScore > 2 ? 'Significant' : driftScore > 1 ? 'Moderate' : 'Minimal';
  const driftColor = driftScore > 3 ? '#f87171' : driftScore > 2 ? '#fb923c' : driftScore > 1 ? '#fbbf24' : '#34d399';

  // AI executive diagnostic often contains drift insights
  const diagnostic = ai.executiveDiagnostic || '';
  const businessPage = ai.businessInOnePage || '';

  return (
    <div>
      <h2 className="font-display text-xl text-white mb-1">Concept Drift Detector</h2>
      <p className="text-xs text-neutral-500 mb-6">Where the brand promise diverges from guest reality</p>

      {/* Drift Score */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-white">Drift Index</h3>
            <p className="text-xs text-neutral-500">Gap between concept intent and guest perception</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-black" style={{ color: driftColor }}>{driftScore.toFixed(1)}</p>
            <p className="text-xs" style={{ color: driftColor }}>{driftLevel} Drift</p>
          </div>
        </div>

        {/* Visual: Concept vs Reality bar */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 w-24">Concept Clarity</span>
            <div className="flex-1 h-3 bg-neutral-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(conceptScore / 10) * 100}%`, backgroundColor: getScoreColor(conceptScore).hex }} />
            </div>
            <span className="text-xs font-bold" style={{ color: getScoreColor(conceptScore).hex }}>{conceptScore.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 w-24">Brand Expression</span>
            <div className="flex-1 h-3 bg-neutral-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(brandScore / 10) * 100}%`, backgroundColor: getScoreColor(brandScore).hex }} />
            </div>
            <span className="text-xs font-bold" style={{ color: getScoreColor(brandScore).hex }}>{brandScore.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 w-24">Guest Signal</span>
            <div className="flex-1 h-3 bg-neutral-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(guestScore / 10) * 100}%`, backgroundColor: getScoreColor(guestScore).hex }} />
            </div>
            <span className="text-xs font-bold" style={{ color: getScoreColor(guestScore).hex }}>{guestScore.toFixed(1)}</span>
          </div>
        </div>
      </div>

      {/* What They Say vs What Guests Say */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-brand-gold mb-3 uppercase tracking-wider">Brand Claims</h3>
          <p className="text-xs text-neutral-500 mb-2">What the business says it is:</p>
          {brandClaims.length > 0 ? brandClaims.map((c, i) => (
            <p key={i} className="text-xs text-neutral-400 mb-1">{c}</p>
          )) : (
            <p className="text-xs text-neutral-600">Run AI audit to populate</p>
          )}
        </div>
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-neutral-300 mb-3 uppercase tracking-wider">Guest Reality</h3>
          <p className="text-xs text-neutral-500 mb-2">What guests actually experience:</p>
          {guestReality.length > 0 ? guestReality.map((g, i) => (
            <p key={i} className={`text-xs mb-1 ${g.startsWith('+') ? 'text-emerald-400/80' : 'text-red-400/80'}`}>{g}</p>
          )) : (
            <p className="text-xs text-neutral-600">Run AI audit with Google reviews to populate</p>
          )}
        </div>
      </div>

      {/* Coherence Analysis */}
      {brandCoherence.overallNotes && (
        <div className="card p-4 mb-6">
          <h3 className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">Coherence Analysis</h3>
          <p className="text-xs text-neutral-400 leading-relaxed">{brandCoherence.overallNotes}</p>
        </div>
      )}

      {/* Score Tensions = Drift Evidence */}
      {tensions.length > 0 && (
        <div className="card p-4 mb-6">
          <h3 className="text-xs font-semibold text-amber-400 mb-2 uppercase tracking-wider">Drift Evidence</h3>
          {tensions.map((t, i) => (
            <p key={i} className="text-xs text-neutral-400 mb-1 flex items-start gap-2">
              <span className="text-amber-400">!</span> {t}
            </p>
          ))}
        </div>
      )}

      {/* AI Diagnostic */}
      {diagnostic && (
        <div className="card p-4 border-l-3 border-brand-gold">
          <h3 className="text-xs font-semibold text-brand-gold mb-2 uppercase tracking-wider">AI Assessment</h3>
          <p className="text-xs text-neutral-300 leading-relaxed">{diagnostic}</p>
          {businessPage && <p className="text-xs text-neutral-400 leading-relaxed mt-2">{businessPage}</p>}
        </div>
      )}
    </div>
  );
}
